import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "probleme-outlook-gmail",
  silo: "recuperation",
  h1: "Problème Outlook ou Gmail sur PC : messagerie qui ne fonctionne plus",
  metaTitle: "Outlook ou Gmail qui ne marche plus : solutions",
  metaDescription: "Votre messagerie Outlook ou Gmail plante, ne reçoit plus de mails ? Voici les causes les plus fréquentes et comment les résoudre rapidement.",
  excerpt: "Outlook qui plante, Gmail qui ne se charge plus, mails bloqués dans les envois... Les problèmes de messagerie sont parmi les plus paralysants au quotidien. Voici les solutions par cas.",
  publishedAt: "2024-01-12",
  readingMinutes: 6,
  relatedSlugs: ["recuperer-fichiers-supprimes", "logiciel-plante-ne-souvre-pas", "internet-lent-instable", "erreurs-windows-frequentes", "applications-web-m365-google-workspace"],
  faq: [
    {
      question: "Outlook ne s'ouvre plus et reste bloqué sur l'écran de chargement -- comment faire ?",
      answer: "Lancez Outlook en mode sans complément : cherchez 'Outlook' dans le menu Démarrer, faites Ctrl+clic dessus ou ajoutez /safe dans la ligne de commande. Si ça fonctionne, un complément est en cause. Désactivez-les un par un dans Fichier > Options > Compléments.",
    },
    {
      question: "Je ne reçois plus de mails depuis hier -- est-ce un problème côté serveur ?",
      answer: "Vérifiez d'abord sur webmail.votredomaine.com ou mail.google.com si les mails arrivent bien côté serveur. Si oui, le problème est dans votre client de messagerie (Outlook, Thunderbird). Si non, le problème est côté serveur ou votre boîte est pleine.",
    },
    {
      question: "Mes mails partent dans les spams chez mes correspondants -- comment arranger ça ?",
      answer: "C'est un problème de réputation de votre serveur d'envoi, pas de votre PC. Si vous utilisez un domaine professionnel, vérifiez les enregistrements SPF, DKIM et DMARC de votre domaine. Nos techniciens peuvent vérifier et corriger cette configuration.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Les problèmes de messagerie peuvent venir de nombreux endroits : le logiciel lui-même, la connexion internet, le serveur de messagerie, ou la configuration du compte. Voici comment identifier rapidement d'où vient le problème.",
    },
    {
      type: "h2",
      text: "Première étape : identifier la source du problème",
    },
    {
      type: "p",
      text: "Ouvrez votre messagerie directement dans un navigateur (webmail) :\n• Gmail : mail.google.com\n• Outlook.com : outlook.live.com\n• Email professionnel : connectez-vous à l'adresse webmail de votre hébergeur\nSi le webmail fonctionne mais pas votre application, le problème est dans le logiciel. Si le webmail ne fonctionne pas non plus, le problème est ailleurs (compte, connexion, serveur).",
    },
    {
      type: "h2",
      text: "Problèmes courants avec Outlook",
    },
    {
      type: "h3",
      text: "Outlook ne s'ouvre plus",
    },
    {
      type: "p",
      text: "Tentez de lancer Outlook en mode sans échec (Outlook.exe /safe dans Exécuter). Si ça fonctionne, un complément pose problème. Désactivez-les tous dans Fichier > Options > Compléments > Gérer > Compléments COM. Réactivez-les un par un pour identifier le coupable.",
    },
    {
      type: "h3",
      text: "Profil Outlook corrompu",
    },
    {
      type: "p",
      text: "Dans Panneau de configuration > Courrier > Afficher les profils > Ajouter, créez un nouveau profil Outlook. Configurez-y votre compte email. Si le nouveau profil fonctionne, l'ancien était corrompu -- vous pouvez le supprimer.",
    },
    {
      type: "h3",
      text: "Fichier de données Outlook (PST/OST) corrompu",
    },
    {
      type: "p",
      text: "Microsoft fournit un outil de réparation : SCANPST.EXE (cherchez-le dans le dossier d'installation d'Office). Il analyse et répare les fichiers de données Outlook. Assurez-vous de faire une copie du fichier avant d'exécuter la réparation.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "Sauvegardez votre fichier PST avant toute manipulation",
      text: "Le fichier PST contient tous vos emails, contacts et calendriers Outlook. Avant toute opération, copiez ce fichier (généralement dans Documents\\Fichiers Outlook) sur un disque externe.",
    },
    {
      type: "h2",
      text: "Problèmes courants avec Gmail dans le navigateur",
    },
    {
      type: "h3",
      text: "Gmail ne charge plus",
    },
    {
      type: "p",
      text: "Videz le cache de votre navigateur (Ctrl + Maj + Suppr > Vider le cache). Essayez un autre navigateur. Vérifiez votre [connexion internet](slug:internet-lent-instable) -- Gmail a besoin d'une connexion stable.",
    },
    {
      type: "h3",
      text: "Gmail bloque sur 'Chargement' ou tourne en rond",
    },
    {
      type: "p",
      text: "Accédez à Gmail en mode de base : ajoutez ?nocheckbrowser à l'URL ou accédez à mail.google.com/?ui=html. Si ça fonctionne, le problème vient d'une extension de navigateur. Désactivez les extensions une par une.",
    },
    {
      type: "h2",
      text: "Mails bloqués dans la boîte d'envoi",
    },
    {
      type: "p",
      text: "Des mails bloqués à l'envoi signalent souvent un problème de paramètres SMTP (serveur sortant). Vérifiez que le mot de passe du compte n'a pas été changé récemment. Si votre messagerie est professionnelle, vérifiez les paramètres SMTP avec votre hébergeur.",
    },
    {
      type: "h2",
      text: "Récupération de mails importants",
    },
    {
      type: "p",
      text: "Si des emails ont disparu, cherchez d'abord dans les dossiers Spam et Corbeille de votre messagerie. Gmail conserve les emails supprimés 30 jours dans la corbeille. Pour une [récupération plus avancée](slug:recuperer-fichiers-supprimes), notamment pour des emails supprimés depuis longtemps, nos techniciens peuvent explorer des options de récupération supplémentaires.",
    },
  ],
};

export default article;
